import { EUpdateModalButton, EUpdateModalSubTitle, EUpdateModalTitle } from 'lib/storage';
import { IStoreState } from 'store';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import apiService from 'services/api';
import { getAccessToken } from 'lib/auth';
import apiRoutes from 'services/api/routes';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import moment from 'moment';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { EAddNewUpdateActionType, IUpdateContent, IAddNewUpdateInput, IProductReleasesData } from './types';

interface ReduxAction<T> {
  type: string;
  payload: T;
}

export const setAddNewUpdateModalIsOpen = (
  isModalOpen: boolean,
  modalTitle: EUpdateModalTitle,
  updateModalButtonTittle?: EUpdateModalButton,
  updateModalSubTitle?: EUpdateModalSubTitle,
  productReleaseObj?: IProductReleasesData
) => ({
  type: EAddNewUpdateActionType.SET_IS_MODAL_OPEN_ADD_NEW,
  payload: { isModalOpen, modalTitle, updateModalButtonTittle, updateModalSubTitle, productReleaseObj },
});

export const saveTitle = (title: string) => ({
  type: EAddNewUpdateActionType.SET_UPDATE_TITLE,
  payload: { title },
});

export const setPostUpdateModalIsOpen = (isPostUpdateModalOpen: boolean) => ({
  type: EAddNewUpdateActionType.SET_CONFIRM_IS_MODAL_OPEN_ADD_NEW,
  payload: { isPostUpdateModalOpen },
});

export const setUpdateIds = (newUpdateIds: string) => (dispatch: ThunkDispatch<void, void, AnyAction>) => {
  dispatch({
    type: EAddNewUpdateActionType.SET_UPDATE_IDS,
    payload: newUpdateIds,
  });
};

export const setDeleteModalIsOpen = (isDeleteUpdateModalOpen: boolean) => ({
  type: EAddNewUpdateActionType.SET_DELETE_UPDATE_IS_MODAL_OPEN_ADD_NEW,
  payload: { isDeleteUpdateModalOpen },
});

export const setDeleteUpdateModalIsOpen = (isDeleteUpdateModalOpen: boolean, productReleaseId?: string) => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setDeleteModalIsOpen(isDeleteUpdateModalOpen));
    if (productReleaseId) {
      dispatch(setUpdateIds(productReleaseId));
    }
  };
};

export const setPostUpdateConfirmButtonPressed = (isPostUpdateConfirmButtonPressed: boolean) => ({
  type: EAddNewUpdateActionType.POST_UPDATE_CONFIRM_BUTTON_PRESSED,
  payload: { isPostUpdateConfirmButtonPressed },
});

export const setUpdateContent = (updateContent: IUpdateContent[]) => (
  dispatch: ThunkDispatch<void, void, AnyAction>
) => {
  dispatch({
    type: EAddNewUpdateActionType.SET_UPDATE_CONTENT,
    payload: updateContent,
  });
};

export function updateProductReleaseObj(proRelObj: IProductReleasesData[]) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    const uniqueIds: string[] = [];
    const uniqueProRelObj = proRelObj.filter(obj => {
      const isDuplicate = uniqueIds.includes(obj.id);
      if (!isDuplicate) {
        uniqueIds.push(obj.id);
        return true;
      }
      return false;
    });

    uniqueProRelObj.sort((a, b) => {
      return moment(b.createdAt).diff(a.createdAt);
    });

    dispatch({
      type: EAddNewUpdateActionType.GET_PRODUCT_UPDATES_SUCCESS,
      payload: uniqueProRelObj,
    });
  };
}

export function getProductDescrFromBlob(productReleases: IProductReleasesData[]) {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    const tempProductReleases = [] as IProductReleasesData[];
    productReleases.forEach(proRelObj => {
      const proRelObjTemp = proRelObj;
      const headers = {
        method: 'GET',
      };
      fetch(proRelObj.description, headers)
        .then(response => response.text())
        .then(response => {
          if (response) {
            proRelObjTemp.description = response;
            tempProductReleases.push(proRelObjTemp);
            dispatch(updateProductReleaseObj(tempProductReleases));
          }
        })
        .catch(error => {
          const errorMessage = getAxiosErrorMessage(error);
          handleError(errorMessage);
        });
    });
  };
}

export const getproductReleases = () => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>) => {
    dispatch(setLoading(ELoadingType.SPINNER));
    apiService
      .get(`${apiRoutes.productRelease}s`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(response => {
        if (response && response.data.length > 0) {
          dispatch(getProductDescrFromBlob(response.data));
        } else {
          dispatch({
            type: EAddNewUpdateActionType.GET_PRODUCT_UPDATES_SUCCESS,
            payload: [],
          });
        }
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const postNewUpdate = () => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));

    const { addNewUpdate } = getState();
    const { updateIds, title, editorhtml } = addNewUpdate;
    let url = '';
    const postBody: IAddNewUpdateInput = {
      title,
      description: editorhtml,
    };
    if (updateIds === '') {
      url = apiRoutes.productRelease;
    } else {
      url = `${apiRoutes.productRelease}/${updateIds}`;
    }
    apiService
      .post(url, postBody, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(response => {
        dispatch(setUpdateContent(response.data));
        dispatch(setPostUpdateModalIsOpen(false));
        dispatch(getproductReleases());
        dispatch({
          type: EAddNewUpdateActionType.UPDATE_PRODUCT_SUCCESS,
        });
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      });
    // .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const removeUpdateAndId = () => {
  return (dispatch: ThunkDispatch<void, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));

    const { addNewUpdate } = getState();
    const { updateIds } = addNewUpdate;

    apiService
      .delete(`${apiRoutes.productRelease}/${updateIds}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(_response => {
        dispatch(getproductReleases());
        dispatch({
          type: EAddNewUpdateActionType.DELETE_PRODUCT_SUCCESS,
        });
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      });
    //.finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const createActionCreator = (type: string) => <T>(payload?: T): ReduxAction<T> => ({ type, payload: payload! });

export const saveEditorData = createActionCreator(EAddNewUpdateActionType.SAVE_EDITOR_DATA);
